import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useOnboarding, ProfileSetupModal, OnboardingChecklist } from './onboarding';
import { Button } from '../components/ui/button';
import UserAvatar from './UserAvatar';
import LanguageSwitcher from './LanguageSwitcher';
import { useRestoreRequestCount } from '../hooks/useRestoreRequestCount';
import {
  LayoutDashboard, Calculator, Calendar, FileText, MessageCircle, Settings,
  LogOut, Menu, X, Receipt, Building, BarChart3, Image, Shield, Car, Users,
  TrendingUp, PiggyBank, BarChart2,
  Package, Building2, HelpCircle, Target, BadgeDollarSign, Files, Landmark, Gift
} from 'lucide-react';

// Grouped navigation structure
const navGroups = [
  {
    title: 'Overview',
    items: [
      { path: '/dashboard', labelKey: 'nav.dashboard', icon: LayoutDashboard },
      { path: '/reports', labelKey: 'nav.reports', icon: BarChart3 },
      { path: '/tax-package', labelKey: 'nav.tax_package', icon: Package },
      { path: '/strategy', labelKey: 'nav.strategy', icon: Target },
    ]
  },
  {
    title: 'Financials',
    items: [
      { path: '/transactions', labelKey: 'nav.transactions', icon: Receipt },
      { path: '/bank-connections', labelKey: 'nav.bank_connections', icon: Landmark },
      { path: '/receipts', labelKey: 'nav.receipts', icon: Image },
    ]
  },
  {
    title: 'Tax Operations',
    items: [
      { path: '/deductions', labelKey: 'nav.deductions', icon: Calculator },
      { path: '/quarterly', labelKey: 'nav.quarterly', icon: Calendar },
      { path: '/quarterly-tax', labelKey: 'nav.quarterly_tax', icon: BarChart2 },
      { path: '/mileage', labelKey: 'nav.mileage', icon: Car },
      { path: '/contractors', labelKey: 'nav.contractors', icon: Users },
      { path: '/businesses', labelKey: 'nav.businesses', icon: Building },
    ]
  },
  {
    title: 'Planning',
    items: [
      { path: '/tools/retirement', labelKey: 'nav.retirement', icon: PiggyBank },
      { path: '/cash-flow', labelKey: 'nav.cash_flow', icon: TrendingUp },
    ]
  },
  {
    title: 'Resources',
    items: [
      { path: '/documents', labelKey: 'nav.documents', icon: FileText },
      { path: '/chat', labelKey: 'nav.ai_chat', icon: MessageCircle },
      { path: '/audit-report', labelKey: 'nav.audit_report', icon: Shield },
      { path: '/referrals', labelKey: 'nav.referrals', icon: Gift },
      { path: '/help', labelKey: 'nav.help', icon: HelpCircle },
    ]
  },
  {
    title: 'System',
    items: [
      { path: '/settings', labelKey: 'nav.settings', icon: Settings },
    ]
  },
];

const DashboardLayout = ({ children }) => {
  const { t } = useTranslation();
  const { user, logout, getAuthHeaders } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [isAdmin, setIsAdmin] = React.useState(false);
  const restoreRequests = useRestoreRequestCount(isAdmin);
  const { showProfileSetup, onProfileSetupComplete, closeProfileSetup, toggleChecklist, onboardingData, loading: onboardingLoading } = useOnboarding();

  React.useEffect(() => {
    // WS-3.2: Admin status is now read directly from the user object returned by
    // is_admin comes from /auth/me and login responses (admin_accounts lookup).
    if (!user) { setIsAdmin(false); return; }
    setIsAdmin(user.is_admin === true);
  }, [user]);

  const handleLogout = async () => { await logout(); navigate('/'); };

  const NavLink = ({ item, onClick, mobile }) => {
    const isActive = location.pathname === item.path;
    if (mobile) {
      return (
        <Link key={item.path} to={item.path}
          className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all text-sm ${
            isActive ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-stone-600 hover:bg-stone-100'
          }`}
          onClick={onClick}
          data-testid={`nav-${item.path.slice(1)}`}
          aria-current={isActive ? 'page' : undefined}
        >
          <item.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-emerald-600' : 'text-stone-400'}`} aria-hidden="true" />
          <span>{t(item.labelKey)}</span>
        </Link>
      );
    }
    return (
      <Link key={item.path} to={item.path}
        className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm ${
          isActive
            ? 'bg-emerald-900/30 text-emerald-400 border-l-2 border-emerald-500 pl-[10px]'
            : 'text-stone-400 hover:bg-stone-800 hover:text-stone-100'
        }`}
        data-testid={`nav-${item.path.slice(1)}`}
        aria-current={isActive ? 'page' : undefined}
        aria-label={t(item.labelKey)}
      >
        <item.icon className="w-[18px] h-[18px] flex-shrink-0" aria-hidden="true" />
        <span>{t(item.labelKey)}</span>
      </Link>
    );
  };

  return (
    <div className="min-h-screen flex">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-emerald-600 focus:text-white focus:rounded-lg">
        Skip to main content
      </a>
      <ProfileSetupModal isOpen={showProfileSetup} onComplete={onProfileSetupComplete} onSkip={closeProfileSetup} />

      {/* D3: Resume-onboarding button — hidden on /chat to avoid covering the send button */}
      {!onboardingLoading && !showProfileSetup && onboardingData?.progress?.phase !== 'completed' && !onboardingData?.progress?.never_show_again && !location.pathname.startsWith('/chat') && (
        <button
          onClick={toggleChecklist}
          data-testid="resume-onboarding-btn"
          className="fixed bottom-24 right-4 z-40 bg-emerald-700 text-white text-xs font-semibold px-3 py-2 rounded-full shadow-lg hover:bg-emerald-600 transition flex items-center gap-1.5"
        >
          <span>📋</span> Resume Setup
        </button>
      )}
      <OnboardingChecklist />

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-60 text-stone-400 border-r border-stone-800 flex-shrink-0" style={{ background: '#10172a' }} role="navigation" aria-label="Main navigation">
        {/* Logo */}
        <div className="px-5 py-5 border-b border-stone-800">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="sidebar-logo" aria-label="SolelyTax - Go to Dashboard">
            <div className="w-9 h-9 relative flex items-center justify-center flex-shrink-0">
              <div className="absolute inset-0 bg-emerald-500 rounded-xl transform rotate-45"></div>
              <div className="relative w-3.5 h-3.5 z-10" style={{ background: '#10172a' }}></div>
            </div>
            <span className="text-lg font-semibold">
              <span className="text-white">Solely</span>
              <span className="text-emerald-400">Tax</span>
            </span>
          </Link>
        </div>

        {/* Grouped Navigation */}
        <nav className="flex-1 py-3 overflow-y-auto" aria-label="Primary">
          {navGroups.map((group) => (
            <div key={group.title} className="mb-1">
              <p className="text-[10px] font-bold uppercase tracking-widest text-stone-600 px-5 pt-4 pb-1.5">{group.title}</p>
              <div className="px-2 space-y-0.5">
                {group.items.map((item) => <NavLink key={item.path} item={item} />)}
              </div>
            </div>
          ))}
          {isAdmin && (
            <div className="px-2 mt-1 pt-2 border-t border-stone-800 mx-3">
              <Link to="/admin"
                className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all text-sm ${
                  location.pathname === '/admin'
                    ? 'bg-amber-900/30 text-amber-400 border-l-2 border-amber-500 pl-[10px]'
                    : 'text-amber-500/70 hover:bg-stone-800 hover:text-amber-400'
                }`}
                data-testid="nav-admin" aria-current={location.pathname === '/admin' ? 'page' : undefined}
              >
                <Shield className="w-[18px] h-[18px] flex-shrink-0" aria-hidden="true" />
                <span>Admin</span>
                {restoreRequests > 0 && (
                  <span className="ml-auto rounded-full bg-amber-500 text-stone-950 text-[10px] font-bold px-1.5 min-w-[18px] text-center"
                    title={`${restoreRequests} open restore request(s)`} data-testid="nav-admin-restore-badge">{restoreRequests}</span>
                )}
              </Link>
            </div>
          )}
        </nav>

        {/* User Section */}
        <div className="p-3 border-t border-stone-800">
          <div className="px-2 pb-2">
            <LanguageSwitcher variant="buttons" className="w-full justify-center" />
          </div>
          <div className="flex items-center gap-2.5 px-2 py-2">
            <UserAvatar user={user} className="w-8 h-8" fallbackClassName="text-xs" testId="sidebar-user-avatar" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-stone-200 truncate">{user?.name}</p>
              <p className="text-[10px] text-stone-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-stone-500 hover:text-white hover:bg-stone-800 text-sm h-9 mt-1"
            onClick={handleLogout} data-testid="logout-btn" aria-label={t('nav.logout')}>
            <LogOut className="w-4 h-4 mr-2" aria-hidden="true" />{t('nav.logout')}
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0 bg-white">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center justify-between p-3 border-b border-stone-200 bg-white">
          <Link to="/dashboard" className="flex items-center gap-2" aria-label="SolelyTax - Go to Dashboard">
            <div className="w-8 h-8 relative flex items-center justify-center" aria-hidden="true">
              <div className="absolute inset-0 bg-emerald-500 rounded-lg transform rotate-45"></div>
              <div className="relative w-3 h-3 bg-stone-900 z-10"></div>
            </div>
            <span className="font-semibold text-sm">
              <span className="text-stone-900">Solely</span><span className="text-emerald-600">Tax</span>
            </span>
          </Link>
          <div className="flex items-center gap-1">
            <LanguageSwitcher variant="compact" />
            <Link to="/settings" aria-label="Your profile settings" className="p-1" data-testid="mobile-header-avatar-link">
              <UserAvatar user={user} className="w-7 h-7" fallbackClassName="text-[10px]" testId="mobile-header-avatar" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn" className="text-stone-700"
              aria-expanded={mobileMenuOpen} aria-controls="mobile-menu"
              aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}>
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </header>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <nav id="mobile-menu" className="lg:hidden bg-white border-b border-stone-200 px-3 py-2 max-h-[80vh] overflow-y-auto" role="navigation" aria-label="Mobile navigation">
            {navGroups.map((group) => (
              <div key={group.title} className="mb-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-stone-400 px-4 pt-3 pb-1">{group.title}</p>
                {group.items.map((item) => <NavLink key={item.path} item={item} onClick={() => setMobileMenuOpen(false)} mobile />)}
              </div>
            ))}
            {isAdmin && (
              <div className="border-t border-stone-200 mt-2 pt-2">
                <Link to="/admin" className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm ${
                  location.pathname === '/admin' ? 'bg-amber-50 text-amber-700 font-medium' : 'text-amber-600 hover:bg-amber-50'
                }`} onClick={() => setMobileMenuOpen(false)} data-testid="nav-admin">
                  <Shield className="w-4 h-4 flex-shrink-0" /><span>Admin</span>
                  {restoreRequests > 0 && (
                    <span className="ml-auto rounded-full bg-amber-500 text-white text-[10px] font-bold px-1.5 min-w-[18px] text-center"
                      data-testid="mobile-nav-admin-restore-badge">{restoreRequests}</span>
                  )}
                </Link>
              </div>
            )}
            <div className="border-t border-stone-200 mt-2 pt-2">
              <Button variant="ghost" className="w-full justify-start gap-3 px-4 py-2.5 rounded-lg text-red-600 hover:bg-red-50 text-sm"
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>
                <LogOut className="w-4 h-4 flex-shrink-0" /><span>{t('auth.logout')}</span>
              </Button>
            </div>
          </nav>
        )}

        {/* Page Content */}
        <main id="main-content" className="flex-1 p-4 md:p-6 lg:p-8 overflow-x-hidden overflow-y-auto bg-stone-50 text-stone-900" role="main" tabIndex={-1}>
          <div className="max-w-full">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
